import { createApi } from '@reduxjs/toolkit/query/react';
import { appBaseQuery } from './baseQuery';
import type {
  ActivityFeed,
  AppProfile,
  CreateLinkInput,
  Link,
  LinkAnalytics,
  ListLinksParams,
  ListLinksResult,
} from '../types/link';

export const linksApi = createApi({
  reducerPath: 'linksApi',
  baseQuery: appBaseQuery,
  tagTypes: ['Link', 'LinkList', 'Analytics', 'AppProfile'],
  endpoints: (build) => ({
    listLinks: build.query<ListLinksResult, ListLinksParams>({
      query: (params) => ({ op: 'listLinks', params }),
      providesTags: ['LinkList'],
    }),
    getLink: build.query<Link, string>({
      query: (id) => ({ op: 'getLink', id }),
      providesTags: (_res, _err, id) => [{ type: 'Link', id }],
    }),
    createLink: build.mutation<Link, CreateLinkInput>({
      query: (input) => ({ op: 'createLink', input }),
      invalidatesTags: ['LinkList'],
    }),
    expireLink: build.mutation<Link, string>({
      query: (id) => ({ op: 'expireLink', id }),
      invalidatesTags: (_res, _err, id) => [{ type: 'Link', id }, 'LinkList'],
    }),
    getAnalytics: build.query<LinkAnalytics, { id: string; windowDays: number }>({
      query: ({ id, windowDays }) => ({ op: 'getAnalytics', id, windowDays }),
      providesTags: (_res, _err, { id }) => [{ type: 'Analytics', id }],
    }),
    getActivity: build.query<ActivityFeed, { limit?: number; days?: number } | void>({
      // `arg` is typed `T | void`, and optional-chaining directly on a void-inclusive
      // union doesn't narrow the way it does for `T | undefined` — normalize first.
      query: (arg) => {
        const { limit, days } = arg || {};
        return { op: 'getActivity', limit, days };
      },
    }),
    getAppProfile: build.query<AppProfile, void>({
      query: () => ({ op: 'getAppProfile' }),
      transformResponse: (res: { profile: AppProfile }) => res.profile,
      providesTags: ['AppProfile'],
    }),
    saveAppProfile: build.mutation<AppProfile, AppProfile>({
      query: (profile) => ({ op: 'saveAppProfile', profile }),
      transformResponse: (res: { profile: AppProfile }) => res.profile,
      // App fields are merged into every link → bust the cached list too.
      invalidatesTags: ['AppProfile', 'LinkList'],
    }),
  }),
});

export const {
  useListLinksQuery,
  useGetLinkQuery,
  useCreateLinkMutation,
  useExpireLinkMutation,
  useGetAnalyticsQuery,
  useGetActivityQuery,
  useGetAppProfileQuery,
  useSaveAppProfileMutation,
} = linksApi;
